import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Package, DollarSign, AlertTriangle,
  Clock, Target, ChevronRight, Download, Loader2,
} from 'lucide-react';
import VisualTimeline from './VisualTimeline';

const formatINR = (value) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value || 0);

const formatPct = (value) => `${(value * 100).toFixed(1)}%`;

const FUNNEL_STAGES = [
  { key: 'total_assigned', label: 'Assigned', color: 'bg-gray-100 border-gray-300 text-gray-700', iconColor: 'text-gray-500' },
  { key: 'total_tl_verified', label: 'TL Verified', rateKey: 'tl_verify', color: 'bg-blue-100 border-blue-300 text-blue-700', iconColor: 'text-blue-500' },
  { key: 'total_manager_verified', label: 'Manager Approved', rateKey: 'manager_verify', color: 'bg-indigo-100 border-indigo-300 text-indigo-700', iconColor: 'text-indigo-500' },
  { key: 'total_ops_delivered', label: 'Delivered to Gym', rateKey: 'delivery', color: 'bg-purple-100 border-purple-300 text-purple-700', iconColor: 'text-purple-500' },
  { key: 'total_customer_received', label: 'Customer Received', rateKey: 'pickup', color: 'bg-green-100 border-green-300 text-green-700', iconColor: 'text-green-500' },
];

export default function AnalyticsTab({ user }) {
  const [analytics, setAnalytics] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gymFilter, setGymFilter] = useState('all');

  // Timeline dialog for escalation rows
  const [showTimeline, setShowTimeline] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      const params = {};
      if (gymFilter && gymFilter !== 'all') params.gym_id = gymFilter;
      const res = await axiosInstance.get('/supplements/analytics', { params });
      const data = res.data || {};
      setAnalytics(data);
      // Extract unique gym names from by_gym breakdown for the gym filter dropdown
      if (data.by_gym && Array.isArray(data.by_gym)) {
        setGyms(data.by_gym.map(g => ({ gym_id: g.gym_id || g._id, gym_name: g.gym_name || g._id })));
      }
    } catch {
      toast.error('Failed to load analytics');
    }
  }, [gymFilter]);

  const fetchEscalations = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/supplements/escalations');
      setEscalations(res.data?.escalations || []);
    } catch {
      toast.error('Failed to load escalations');
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchAnalytics(), fetchEscalations()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchAnalytics, fetchEscalations]);

  useEffect(() => {
    if (!loading) fetchAnalytics();
  }, [gymFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTimeline = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
    setShowTimeline(true);
  };

  const handleExportEscalations = async () => {
    if (!escalations.length) return;
    setExporting(true);
    try {
      const headers = ['Customer Name', 'Gym', 'Stack', 'Days Overdue', 'Escalation Count', 'Status'];
      const rows = escalations.map(e => [
        e.customer_name || '',
        e.gym_name || '',
        e.stack_name || '',
        e.days_overdue || 0,
        e.escalation_count || 0,
        e.status || '',
      ]);
      const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `escalations-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Escalations exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  const data = analytics || {};
  const funnel = data.funnel || data;
  const conversionRates = data.conversion_rates || {};
  const costAnalysis = data.cost_analysis || {};
  const gymBreakdown = (data.by_gym || data.gym_breakdown || []).map(g => ({
    ...g,
    total_assigned: g.total_assigned || g.total || 0,
    membership_revenue: g.membership_revenue || g.total_membership_revenue || 0,
    cogs: g.cogs || g.total_cogs || 0,
  }));
  const avgDeliveryTime = data.avg_delivery_hours || data.avg_delivery_time;
  const stageBreakdown = data.stage_times || {};

  // End-to-end completion rate
  const totalAssigned = funnel.total_assigned || 0;
  const totalCompleted = funnel.total_customer_received || 0;
  const completionRate = totalAssigned > 0 ? totalCompleted / totalAssigned : 0;

  // Cost metrics
  const totalCogs = costAnalysis.total_cogs || 0;
  const membershipRevenue = costAnalysis.membership_revenue || costAnalysis.total_revenue || 0;
  const margin = membershipRevenue - totalCogs;
  const marginPct = membershipRevenue > 0 ? margin / membershipRevenue : 0;

  // Overdue count
  const overdueCount = (Array.isArray(escalations) ? escalations : []).length;

  return (
    <div>
      {/* Header with Gym Filter */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Analytics Dashboard</h2>
          <p className="text-sm text-muted-foreground">Funnel metrics, cost analysis, and SLA tracking</p>
        </div>
        {gyms.length > 0 && (
          <Select value={gymFilter} onValueChange={setGymFilter}>
            <SelectTrigger className="w-56" data-testid="analytics-gym-filter">
              <SelectValue placeholder="All Gyms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Gyms</SelectItem>
              {gyms.map(g => (
                <SelectItem key={g.gym_id || g.id} value={g.gym_id || g.id}>{g.gym_name || g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Section 1: Funnel KPI Cards */}
      <h3 className="text-md font-semibold text-slate-700 mb-3">Delivery Funnel</h3>
      <div className="flex items-center gap-2 mb-2 overflow-x-auto pb-2">
        {FUNNEL_STAGES.map((stage, idx) => {
          const count = funnel[stage.key] || 0;
          const rate = stage.rateKey ? conversionRates[stage.rateKey] : null;
          return (
            <React.Fragment key={stage.key}>
              {idx > 0 && (
                <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
              )}
              <Card className={`min-w-[160px] flex-1 border-2 ${stage.color}`}>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-xs font-medium mt-1">{stage.label}</div>
                  {rate != null && (
                    <div className="text-xs mt-1 opacity-75">{formatPct(rate)} conversion</div>
                  )}
                </CardContent>
              </Card>
            </React.Fragment>
          );
        })}
      </div>

      {/* Completion Rate + Avg Delivery Time */}
      <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
          <Target className="w-4 h-4 text-green-600" />
          <span className="text-green-800 font-medium">
            {formatPct(completionRate)} end-to-end completion
          </span>
        </div>
        {avgDeliveryTime != null && (
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <Clock className="w-4 h-4 text-blue-600" />
            <span className="text-blue-800 font-medium">
              Avg {Math.round(avgDeliveryTime)} hours from assignment to completion
            </span>
          </div>
        )}
        {Object.keys(stageBreakdown).length > 0 && (
          <div className="text-xs text-slate-500">
            {Object.entries(stageBreakdown).map(([stage, hours]) => (
              <span key={stage} className="mr-3">{stage}: {Math.round(hours)}h</span>
            ))}
          </div>
        )}
      </div>

      {/* Section 2: Cost Analysis Cards */}
      <h3 className="text-md font-semibold text-slate-700 mb-3">Cost Analysis</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 text-slate-500" />
              <span className="text-sm text-slate-500">Total Delivered</span>
            </div>
            <div className="text-2xl font-bold text-slate-800">
              {costAnalysis.total_delivered || funnel.total_customer_received || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-red-500" />
              <span className="text-sm text-red-600">Total COGS</span>
            </div>
            <div className="text-2xl font-bold text-red-700">{formatINR(totalCogs)}</div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span className="text-sm text-green-600">Membership Revenue</span>
            </div>
            <div className="text-2xl font-bold text-green-700">{formatINR(membershipRevenue)}</div>
          </CardContent>
        </Card>

        <Card className={`${margin >= 0 ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              {margin >= 0
                ? <TrendingUp className="w-5 h-5 text-green-500" />
                : <TrendingDown className="w-5 h-5 text-red-500" />}
              <span className={`text-sm ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>Margin</span>
            </div>
            <div className={`text-2xl font-bold ${margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatINR(margin)}
            </div>
            <div className={`text-xs mt-1 ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPct(Math.abs(marginPct))} of revenue
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 3: Per-Gym Breakdown Table */}
      <h3 className="text-md font-semibold text-slate-700 mb-3">Per-Gym Breakdown</h3>
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="text-left px-4 py-3 text-sm font-medium">Gym</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">Assigned</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">Completed</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">Pending</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">In Delivery</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">Revenue</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">COGS</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {gymBreakdown.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                      No gym data available.
                    </td>
                  </tr>
                ) : (
                  <>
                    {[...gymBreakdown]
                      .sort((a, b) => (b.total_assigned || 0) - (a.total_assigned || 0))
                      .map((gym) => {
                        const completed = gym.completed || gym.total_customer_received || 0;
                        const assigned = gym.total_assigned || 0;
                        const completionPct = assigned > 0 ? completed / assigned : 0;
                        const gymMargin = (gym.membership_revenue || 0) - (gym.cogs || 0);
                        let rowBg = '';
                        if (assigned > 0 && completionPct === 1) rowBg = 'bg-green-50';
                        else if (assigned > 0 && completionPct === 0) rowBg = 'bg-red-50';
                        else if (assigned > 0) rowBg = 'bg-amber-50';

                        return (
                          <tr key={gym.gym_id || gym.gym_name} className={`hover:bg-slate-50 ${rowBg}`}>
                            <td className="px-4 py-3 font-medium text-slate-800">{gym.gym_name || '\u2014'}</td>
                            <td className="px-4 py-3 text-right text-slate-700">{assigned}</td>
                            <td className="px-4 py-3 text-right text-green-700 font-medium">{completed}</td>
                            <td className="px-4 py-3 text-right text-slate-600">{gym.pending || 0}</td>
                            <td className="px-4 py-3 text-right text-slate-600">{gym.in_delivery || 0}</td>
                            <td className="px-4 py-3 text-right text-green-700">{formatINR(gym.membership_revenue)}</td>
                            <td className="px-4 py-3 text-right text-red-600">{formatINR(gym.cogs)}</td>
                            <td className={`px-4 py-3 text-right font-medium ${gymMargin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {formatINR(gymMargin)}
                            </td>
                          </tr>
                        );
                      })}
                    {/* Totals row */}
                    {gymBreakdown.length > 1 && (() => {
                      const totals = gymBreakdown.reduce((acc, g) => ({
                        total_assigned: acc.total_assigned + (g.total_assigned || 0),
                        completed: acc.completed + (g.completed || g.total_customer_received || 0),
                        pending: acc.pending + (g.pending || 0),
                        in_delivery: acc.in_delivery + (g.in_delivery || 0),
                        revenue: acc.revenue + (g.membership_revenue || 0),
                        cogs: acc.cogs + (g.cogs || 0),
                      }), { total_assigned: 0, completed: 0, pending: 0, in_delivery: 0, revenue: 0, cogs: 0 });

                      return (
                        <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                          <td className="px-4 py-3 text-slate-800">TOTAL</td>
                          <td className="px-4 py-3 text-right text-slate-800">{totals.total_assigned}</td>
                          <td className="px-4 py-3 text-right text-green-700">{totals.completed}</td>
                          <td className="px-4 py-3 text-right text-slate-700">{totals.pending}</td>
                          <td className="px-4 py-3 text-right text-slate-700">{totals.in_delivery}</td>
                          <td className="px-4 py-3 text-right text-green-700">{formatINR(totals.revenue)}</td>
                          <td className="px-4 py-3 text-right text-red-600">{formatINR(totals.cogs)}</td>
                          <td className={`px-4 py-3 text-right ${totals.revenue - totals.cogs >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatINR(totals.revenue - totals.cogs)}
                          </td>
                        </tr>
                      );
                    })()}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: SLA & Escalations */}
      <h3 className="text-md font-semibold text-slate-700 mb-3">SLA & Escalations</h3>

      {/* SLA Overview Card */}
      <Card className={`mb-4 ${overdueCount > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-6 h-6 ${overdueCount > 0 ? 'text-red-600' : 'text-green-600'}`} />
            {overdueCount > 0 ? (
              <div>
                <span className="text-lg font-bold text-red-700">{overdueCount} overdue deliver{overdueCount !== 1 ? 'ies' : 'y'}</span>
                <p className="text-sm text-red-600">Assignments exceeding 3-day SLA</p>
              </div>
            ) : (
              <div>
                <span className="text-lg font-bold text-green-700">All on track</span>
                <p className="text-sm text-green-600">No overdue deliveries</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Escalation Table */}
      {overdueCount > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-500">Sorted by most urgent first</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportEscalations}
              disabled={exporting}
              data-testid="export-escalations-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              Export CSV
            </Button>
          </div>
          <Card className="mb-6">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="text-left px-4 py-3 text-sm font-medium">Customer</th>
                      <th className="text-left px-4 py-3 text-sm font-medium">Gym</th>
                      <th className="text-left px-4 py-3 text-sm font-medium">Stack</th>
                      <th className="text-right px-4 py-3 text-sm font-medium">Days Overdue</th>
                      <th className="text-right px-4 py-3 text-sm font-medium">Escalations</th>
                      <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[...escalations]
                      .sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0))
                      .map((esc, idx) => (
                        <tr
                          key={esc.assignment_id || idx}
                          className="hover:bg-red-50 cursor-pointer"
                          onClick={() => openTimeline(esc.assignment_id)}
                          data-testid={`escalation-row-${idx}`}
                        >
                          <td className="px-4 py-3 font-medium text-slate-800">{esc.customer_name || '\u2014'}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{esc.gym_name || '\u2014'}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{esc.stack_name || '\u2014'}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-red-600 font-bold animate-pulse">{esc.days_overdue || 0}d</span>
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">{esc.escalation_count || 0}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                              {esc.status || 'overdue'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Timeline Dialog (for escalation row clicks) */}
      <Dialog open={showTimeline} onOpenChange={setShowTimeline}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assignment Timeline</DialogTitle>
          </DialogHeader>
          {selectedAssignmentId && (
            <VisualTimeline assignmentId={selectedAssignmentId} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
